import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

/** How often a long-lived tab re-checks for a newly deployed version. */
export const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

/**
 * Toast shown when a new app version has been deployed. Also polls for new
 * service-worker versions hourly and whenever the tab regains visibility —
 * long-lived tabs and installed PWA windows never re-fetch sw.js on their
 * own, which left them running a stale cached build indefinitely.
 */
export function PwaUpdatePrompt() {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, reg) {
      if (reg) setRegistration(reg);
    },
  });

  // Polling lives in an effect so the interval and visibility listener are
  // registered once and removed on unmount.
  useEffect(() => {
    if (!registration) return;
    const checkForUpdate = () => {
      registration.update().catch(() => {
        /* offline / transient network error — retry on the next tick */
      });
    };
    const intervalId = setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [registration]);

  if (!needRefresh) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '1rem',
        left: '50%',
        transform: 'translateX(-50%)',
        background: '#1e3a5f',
        color: 'white',
        padding: '0.75rem 1.25rem',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        zIndex: 9999,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontSize: '0.875rem' }}>New version available</span>
      <button
        onClick={() => updateServiceWorker(true)}
        style={{
          background: 'white',
          color: '#1e3a5f',
          border: 'none',
          padding: '0.35rem 0.75rem',
          borderRadius: '8px',
          fontWeight: 600,
          cursor: 'pointer',
          fontSize: '0.8rem',
        }}
      >
        Update
      </button>
    </div>
  );
}
